import { z } from 'zod'

export const FIGURE_ADDRESS_CORRECTION_REQUIRED =
  'FIGURE_ADDRESS_CORRECTION_REQUIRED'
export const FIGURE_SUBMISSION_REJECTED = 'FIGURE_SUBMISSION_REJECTED'

export interface FigureSubmissionError {
  code: string
  message: string
  retryable: false
}

export interface FigureOffer {
  uuid: string
  minAmount: number
  maxAmount: number
  rate: number
  primeRate: number
  term: number
  rateType: 'FIXED' | 'VARIABLE'
  originationFeePercent: number
  preDiscountOriginationFee?: number
  discounts: Array<{
    type: string
    rateDiscountAmount: number
  }>
}

export interface FigurePropertyValue {
  borrowerEstimatedValue: number
  propertyAvmValue: number
  preLoanCltv: number
  postLoanCltv: number
  preLoanAdjCltv: number
  postLoanAdjCltv: number
  postLoanAdjMinCltv: number
}

export interface FigureSoftCredit {
  provider: string
  score: number
  timestamp: string
}

/**
 * Client-facing offers payload. This is what the borrower's browser receives, so
 * it must never carry raw adverse-action/fraud codes. Only the derived, opaque
 * `reasonCategory` is safe to expose — see `FigureOffersResult` for the
 * server-only superset that still holds the raw reasons.
 */
export interface FigureOffersResponse {
  nextInquiryStep: string
  status: string
  offers?: FigureOffer[]
  propertyValue?: FigurePropertyValue
  softCredit?: FigureSoftCredit
  /** Derived bucket the no-offers UI switches on; see `categorizeFigureReasons`. */
  reasonCategory?: FigureReasonCategory
}

/**
 * Server-only offers result. Extends the client payload with the raw Figure
 * `statusReasons` for logging/debugging. NEVER return this to a borrower — pass
 * it through `toBorrowerFigureOffersResponse` at the HTTP boundary so the raw
 * codes stay server-side.
 */
export interface FigureOffersResult extends FigureOffersResponse {
  statusReasons?: string[]
}

/**
 * Strips server-only fields (raw `statusReasons`) so only the safe, client-facing
 * shape leaves the service layer. Use this in every handler that serializes a
 * Figure offers result to a borrower.
 */
export function toBorrowerFigureOffersResponse(
  result: FigureOffersResult
): FigureOffersResponse {
  const { statusReasons: _serverOnly, ...safe } = result
  return safe
}

export const FigureBannerText: Record<string, string> = {
  UNKNOWN:
    'Figure offers could not be fetched, go to Figure portal for next steps.',
  NO_OFFERS: 'No Figure offers available, go to Figure portal to view.',
  OFFERS_AVAILABLE:
    'This application has been sent to Figure with offers available.',
  BORROWER_AUTH_REQUIRED: 'Borrower must log in to Figure to continue.',
  BROKER_DOC_SIGNATURE_REQUIRED: 'Broker must sign document to continue.',
  INQUIRY_COMPLETE: 'Inquiry complete! Continue application through Figure.',
  INQUIRY_CLOSED:
    'Inquiry has been closed through cancellation or another manual action.',
  INQUIRY_WITHDRAWN: 'Inquiry is expired. Ask client to start a new inquiry.',
  CREDIT_MATCH_REQUIRED: 'Collect SSN from client to continue.',
  MORE_ASSETS_REQUIRED: 'Go to Figure portal for next steps.',
  LIEN_MATCHING_REQUIRED: 'Go to Figure portal for next steps.',
  PROPERTY_VALUE_REQUIRED:
    'Figure could not generate an AVM, check with client to validate property value.',
  PAYOFF_REQUIRED: 'Go to Figure portal for next steps.',
  UNFREEZE_CREDIT: 'Client has a freeze on their credit, unfreeze to continue.',
  SELECT_PROPERTY: 'Check with client to validate property address.',
}

export const FigureBannerStatus: Record<
  string,
  'success' | 'warning' | 'danger'
> = {
  UNKNOWN: 'danger',
  NO_OFFERS: 'warning',
  OFFERS_AVAILABLE: 'success',
  BORROWER_AUTH_REQUIRED: 'success',
  BROKER_DOC_SIGNATURE_REQUIRED: 'success',
  INQUIRY_COMPLETE: 'success',
  INQUIRY_CLOSED: 'danger',
  INQUIRY_WITHDRAWN: 'danger',

  CREDIT_MATCH_REQUIRED: 'warning',
  MORE_ASSETS_REQUIRED: 'warning',
  LIEN_MATCHING_REQUIRED: 'warning',
  PROPERTY_VALUE_REQUIRED: 'warning',
  PAYOFF_REQUIRED: 'warning',
  UNFREEZE_CREDIT: 'warning',
  SELECT_PROPERTY: 'warning',
}

// Minimal type for Figure details API response - only fields we use
export interface FigureDetailsResponse {
  data: {
    organization?: string
    appUuid?: string
    appData: {
      status: string
      overallStatus: string
      nextInquiryStep: string
      autopay?: boolean
      keyDates?: {
        createdDate?: string
        expirationDate?: string
        signingDate?: string
        rescissionEndDate?: string
        completedDate?: string
        canceledDate?: string
        declinedDate?: string
        productAgreementESignedTimestamp?: string
      }
      propertyAddress?: {
        street1?: string
        city?: string
        state?: string
        zip?: string
      }
      statusReasons?: Array<string>
      propertyLiens?: Array<{
        lienUuid?: string
      }>
      propertyDetails?: {
        propertyType?: string
      }
      borrowerDetails?: {
        primary?: {
          email?: string
          firstName?: string
          lastName?: string
          dob?: string
          phoneNumber?: string
          address?: {
            street1?: string
            city?: string
            state?: string
            zip?: string
          }
        }
        additionalSigners?: Array<{
          email?: string
          firstName?: string
          lastName?: string
        }>
      }
      fundingTransactionReport?: {
        transactions?: Array<{
          amount?: number
          initiatedAt?: string
          status?: string
          sentAt?: string
        }>
      }
      underwriting?: {
        selectedOffer?: {
          loanAmount?: number
          drawAmount?: number
          term?: number
          rate?: number
          rateType?: string
          estimatedMonthlyPayment?: number
          originationFee?: number
        }
        softCredit?: {
          score?: number
        }
        monthlyIncome?: number
        postLoanDti?: number
        employmentType?: string
      }
      propertyValue?: {
        borrowerEstimatedValue?: number
        postLoanCltv?: number
        propertyAvmValue?: number
      }
      notary?: {
        fee?: number
        appointmentDate?: string
        completedDate?: string
      }
      costs?: {
        fixedCosts?: Array<{
          type?: string
          borrowerPaidAmount?: number
        }>
      }
      ddpDetails?: {
        totalLiabilityPayoffAmount?: number
      }
      statusHistory?: Array<{
        overallStatus?: string
        status?: string
        timestamp?: string
      }>
    }
    loanOriginatorData?: {
      nmls?: string
      licenses?: {
        loanOriginatorLicense?: {
          licenseHolder?: {
            name?: string
          }
        }
      }
    }
  }
}

/**
 * Figure issues a distinct loan originator UUID per portal (standard / Gold /
 * Concierge) for the same human LO, so the UUID stamped on a lead MUST match the
 * portal whose API key the request uses.
 */
export const FIGURE_TIERS = ['regular', 'gold', 'concierge'] as const

export type FigureTier = (typeof FIGURE_TIERS)[number]

export function figureTierForOrganization(
  organization: string | null | undefined
): FigureTier {
  if (organization === 'SAGE_ROBINHOOD_CONCIERGE') return 'concierge'
  if (organization === 'SAGE_ROBINHOOD_GOLD') return 'gold'
  return 'regular'
}

/** Per-portal Figure loan originator UUIDs, keyed by portal tier. */
export interface FigureLoanOriginatorUuids {
  regular?: string
  gold?: string
  concierge?: string
}

/** Per-portal outcome of a loan-originator UUID sync. */
export interface FigurePortalSyncResult {
  /** Originators returned by Figure for this portal. */
  fetched: number
  /** Loan officers whose NMLS matched a returned Figure originator. */
  matched: number
  /** Tier UUID values newly written (changed) on loan officers. */
  assigned: number
}

/** A loan officer whose UUIDs were written during a sync run. */
export interface FigureUpdatedLoanOfficer {
  id: number
  name: string | null
  email: string | null
  nmls: string | null
  /** Portal UUIDs newly written for this loan officer in this run. */
  assigned: Partial<Record<FigureTier, string>>
}

export interface FigureLoanOriginatorSyncSummary {
  /** Per-portal counts, retained for debugging. */
  portals: Partial<Record<FigureTier, FigurePortalSyncResult>>
  /** Distinct loan_officers rows patched across all synced portals. */
  loanOfficersUpdated: number
  updatedLoanOfficers: FigureUpdatedLoanOfficer[]
}

/**
 * Client-facing categorization of Figure `statusReasons`.
 *
 * Figure returns raw reason codes (~62 observed in production) when a HELOC
 * inquiry yields no offer. We bucket those into a small taxonomy so the
 * no-offers screen can show appropriate messaging and CTAs. Unknown/new codes
 * fall through to `Fallback` by design — see `categorizeFigureReason`.
 */
export const FigureReasonCategory = {
  /** A — Insufficient equity / lien issues; mostly hard eligibility. */
  InsufficientEquity: 'INSUFFICIENT_EQUITY',
  /** B — Credit / affordability decline; adverse-action, do not disclose. */
  CreditDecline: 'CREDIT_DECLINE',
  /** C — Property / valuation / occupancy; mix of fixable and hard. */
  PropertyValuation: 'PROPERTY_VALUATION',
  /** D — Credit access needed; already routed via nextInquiryStep flows. */
  CreditAccess: 'CREDIT_ACCESS',
  /** E — Data corrections / more info; self-serve continue. */
  DataCorrection: 'DATA_CORRECTION',
  /** F — Identity / fraud / risk; sensitive, deliberately opaque. */
  IdentityFraud: 'IDENTITY_FRAUD',
  /** G — Borrower choice / drop-off; re-engagement, not a decline. */
  BorrowerChoice: 'BORROWER_CHOICE',
  /** H — Org/config + anything unrecognized; generic fallback. */
  Fallback: 'FALLBACK',
} as const

export type FigureReasonCategory =
  (typeof FigureReasonCategory)[keyof typeof FigureReasonCategory]

export const figureReasonCategorySchema = z.enum([
  FigureReasonCategory.InsufficientEquity,
  FigureReasonCategory.CreditDecline,
  FigureReasonCategory.PropertyValuation,
  FigureReasonCategory.CreditAccess,
  FigureReasonCategory.DataCorrection,
  FigureReasonCategory.IdentityFraud,
  FigureReasonCategory.BorrowerChoice,
  FigureReasonCategory.Fallback,
])

/**
 * Maps each observed Figure reason code to its category. Keyed by open `string`
 * (not a strict union) so codes Figure adds later resolve to `Fallback` via
 * `categorizeFigureReason` rather than breaking the build. Keys are Figure's
 * literal values, including the space-containing ones — match exactly.
 */
export const FIGURE_REASON_CATEGORY: Record<string, FigureReasonCategory> = {
  // A — Insufficient equity / lien issues
  CLTV: FigureReasonCategory.InsufficientEquity,
  CLTV_ELIGIBILITY_MATRIX: FigureReasonCategory.InsufficientEquity,
  CLTV_LIEN_POSITION: FigureReasonCategory.InsufficientEquity,
  LIEN_POSITION: FigureReasonCategory.InsufficientEquity,
  NEAR_PRIME_LIEN_POSITION: FigureReasonCategory.InsufficientEquity,
  LIEN_TYPE: FigureReasonCategory.InsufficientEquity,
  INV_TAXLIEN: FigureReasonCategory.InsufficientEquity,
  LIEN_VERIFICATION: FigureReasonCategory.InsufficientEquity,
  SENIOR_REVERSE_MORTGAGE: FigureReasonCategory.InsufficientEquity,

  // B — Credit / affordability decline (adverse action — do not disclose)
  CREDITSCORE: FigureReasonCategory.CreditDecline,
  LLC_CREDITSCORE: FigureReasonCategory.CreditDecline,
  INVALIDFICO2: FigureReasonCategory.CreditDecline,
  CREDIT_UTILIZATION: FigureReasonCategory.CreditDecline,
  MORT_TRADES_DELQ: FigureReasonCategory.CreditDecline,
  DELQ60_12: FigureReasonCategory.CreditDecline,
  BANKRUPTCY: FigureReasonCategory.CreditDecline,
  COLLECTION: FigureReasonCategory.CreditDecline,
  PTI: FigureReasonCategory.CreditDecline,
  DTI_ELIGIBILITY_MATRIX: FigureReasonCategory.CreditDecline,
  DTI_DRIFT: FigureReasonCategory.CreditDecline,
  PRODUCT_ELIGIBILITY_MATRIX: FigureReasonCategory.CreditDecline,
  RATE_CAP: FigureReasonCategory.CreditDecline,
  RATE_TOO_HIGH: FigureReasonCategory.CreditDecline,
  RATE_TERM_MAX_OFFER: FigureReasonCategory.CreditDecline,

  // C — Property / valuation / occupancy
  'NO PROPERTY FOUND': FigureReasonCategory.PropertyValuation,
  NOAVMRETURNED: FigureReasonCategory.PropertyValuation,
  NAV_NO_PROPERTY_VALUE: FigureReasonCategory.PropertyValuation,
  AVM_FSD: FigureReasonCategory.PropertyValuation,
  NAV_LANDUSE: FigureReasonCategory.PropertyValuation,
  LAST_SALE_DATE: FigureReasonCategory.PropertyValuation,
  PROPERTY_ACRES: FigureReasonCategory.PropertyValuation,
  UNITS: FigureReasonCategory.PropertyValuation,
  OCCUPANCY_STATUS: FigureReasonCategory.PropertyValuation,
  NEAR_PRIME_OCCUPANCY_STATUS: FigureReasonCategory.PropertyValuation,
  ACTIVE_MLS_LISTING: FigureReasonCategory.PropertyValuation,
  ACTIVE_MLS_LISTING_OFEE: FigureReasonCategory.PropertyValuation,
  PROPERTYOWNERSHIP: FigureReasonCategory.PropertyValuation,
  PROP_OWNERSHIP_DISCREPANCY: FigureReasonCategory.PropertyValuation,

  // D — Credit access needed (already routed via nextInquiryStep flows)
  'SOFT CREDIT LOCKED OR FROZEN': FigureReasonCategory.CreditAccess,
  CREDIT_FROZEN_LOCKED: FigureReasonCategory.CreditAccess,
  'CREDIT NO MATCH SSN': FigureReasonCategory.CreditAccess,
  'CREDIT DATA MISSING OR INVALID': FigureReasonCategory.CreditAccess,
  CREDITINCOMPLETE: FigureReasonCategory.CreditAccess,

  // E — Data corrections / more info (self-serve continue)
  CR_NAME_UPDATE: FigureReasonCategory.DataCorrection,
  CR_ADDRESS_UPDATE: FigureReasonCategory.DataCorrection,
  CR_DOB_UPDATE: FigureReasonCategory.DataCorrection,
  CR_DIFFERENT_OFFER_AMOUNT: FigureReasonCategory.DataCorrection,
  CR_INTERNAL_MISTAKE: FigureReasonCategory.DataCorrection,
  STATED_INCOME_DISCREPANCY: FigureReasonCategory.DataCorrection,
  OTHER_REQUIRED_USER_ENTRY: FigureReasonCategory.DataCorrection,

  // F — Identity / fraud / risk (never disclose)
  SENTILINK_SCORE: FigureReasonCategory.IdentityFraud,
  DNDB_BLACKLIST: FigureReasonCategory.IdentityFraud,
  FACTA_HIT: FigureReasonCategory.IdentityFraud,
  ID_VERIFICATION: FigureReasonCategory.IdentityFraud,
  DUPLICATE_APPLICATION: FigureReasonCategory.IdentityFraud,

  // G — Borrower choice / drop-off
  APPLICANT_DOES_NOT_WISH_TO_PROCEED: FigureReasonCategory.BorrowerChoice,
  DOES_NOT_WANT_FULL_INITIAL_DRAW: FigureReasonCategory.BorrowerChoice,
  DISLIKED_OFFER_LOAN_AMOUNT: FigureReasonCategory.BorrowerChoice,

  // H — Org/config (explicitly mapped; everything unknown also lands here)
  NAV_NOT_PERMITTED_FOR_ORG: FigureReasonCategory.Fallback,
  OTHER: FigureReasonCategory.Fallback,
  UNRECOGNIZED: FigureReasonCategory.Fallback,
  'Contact Support': FigureReasonCategory.Fallback,
}

/**
 * Precedence for resolving a multi-reason response to a single category, most
 * restrictive first. Fraud and credit-decline must dominate so we never show a
 * "continue" path (or a reason) to someone who also tripped those flags.
 *
 * NOTE: this ordering is a compliance/product decision — confirm before ship.
 */
export const FIGURE_CATEGORY_PRIORITY: readonly FigureReasonCategory[] = [
  FigureReasonCategory.IdentityFraud,
  FigureReasonCategory.CreditDecline,
  FigureReasonCategory.CreditAccess,
  FigureReasonCategory.InsufficientEquity,
  FigureReasonCategory.PropertyValuation,
  FigureReasonCategory.DataCorrection,
  FigureReasonCategory.BorrowerChoice,
  FigureReasonCategory.Fallback,
] as const

/**
 * Opaque, loan-officer-only categories (no reason disclosed, no self-serve
 * continue). When an *unmapped* reason code appears, resolution is restricted to
 * these so a new/sensitive code can never surface a "continue"/disclosure screen.
 */
export const FIGURE_OPAQUE_CATEGORIES: readonly FigureReasonCategory[] = [
  FigureReasonCategory.IdentityFraud,
  FigureReasonCategory.CreditDecline,
  FigureReasonCategory.Fallback,
] as const

/** True when Figure sent a code we have not explicitly categorized yet. */
export function isUnmappedFigureReason(reason: string): boolean {
  return !Object.prototype.hasOwnProperty.call(
    FIGURE_REASON_CATEGORY,
    reason.trim()
  )
}

/** Resolves a single Figure reason code to its category, defaulting to Fallback. */
export function categorizeFigureReason(reason: string): FigureReasonCategory {
  const trimmed = reason.trim()
  // hasOwnProperty guard so a code literally named `constructor`/`toString`
  // resolves to Fallback rather than an inherited Object member.
  return Object.prototype.hasOwnProperty.call(FIGURE_REASON_CATEGORY, trimmed)
    ? FIGURE_REASON_CATEGORY[trimmed]
    : FigureReasonCategory.Fallback
}

/**
 * Resolves an array of Figure reason codes to the single highest-precedence
 * category (see `FIGURE_CATEGORY_PRIORITY`). Empty/absent input is Fallback.
 *
 * Safe default for unknown codes: if any reason is unmapped, resolution is
 * restricted to `FIGURE_OPAQUE_CATEGORIES`, so a new/sensitive code mixed with a
 * fixable one (e.g. `CR_ADDRESS_UPDATE`) stays on the opaque loan-officer path
 * instead of disclosing fixable copy + the self-serve Continue CTA. Codes we've
 * *explicitly* mapped to Fallback (e.g. `OTHER`) are not "unmapped" and don't
 * trigger this restriction.
 */
export function categorizeFigureReasons(
  reasons: readonly string[] | null | undefined
): FigureReasonCategory {
  if (!reasons?.length) return FigureReasonCategory.Fallback

  const hasUnmapped = reasons.some(isUnmappedFigureReason)
  const categories = new Set(reasons.map(categorizeFigureReason))
  const candidates = hasUnmapped
    ? FIGURE_CATEGORY_PRIORITY.filter((category) =>
        FIGURE_OPAQUE_CATEGORIES.includes(category)
      )
    : FIGURE_CATEGORY_PRIORITY

  return (
    candidates.find((category) => categories.has(category)) ??
    FigureReasonCategory.Fallback
  )
}

export const FigureCtaAction = {
  ContinueInPortal: 'CONTINUE_IN_PORTAL',
  TalkToLoanOfficer: 'TALK_TO_LOAN_OFFICER',
} as const

export type FigureCtaAction =
  (typeof FigureCtaAction)[keyof typeof FigureCtaAction]

/** Borrower-facing CTA labels. Wording dictated by the ticket; low risk. */
export const FIGURE_CTA_LABEL: Record<FigureCtaAction, string> = {
  [FigureCtaAction.ContinueInPortal]: 'Continue my application', // dark sage green
  [FigureCtaAction.TalkToLoanOfficer]: 'Talk to a loan officer', // white outline
}

export interface FigureCategoryDisposition {
  /** May we show the borrower a reason at all? False = adverse-action/opaque. */
  discloseReason: boolean
  primaryCta: FigureCtaAction
  secondaryCta: FigureCtaAction | null
  /** Offer the refinance alternative alongside the CTAs. */
  showRefiAlternative: boolean
  /** Copy for this category must be legal/compliance-approved before ship. */
  requiresComplianceApproval: boolean
}

/**
 * Machine-readable policy per category: which CTA, whether to disclose a reason,
 * whether legal must sign off. Kept separate from copy so compliance can review
 * routing/disclosure independently of wording.
 */
export const FIGURE_CATEGORY_DISPOSITION: Record<
  FigureReasonCategory,
  FigureCategoryDisposition
> = {
  [FigureReasonCategory.InsufficientEquity]: {
    discloseReason: true,
    primaryCta: FigureCtaAction.TalkToLoanOfficer,
    secondaryCta: null,
    showRefiAlternative: true,
    requiresComplianceApproval: true,
  },
  [FigureReasonCategory.CreditDecline]: {
    discloseReason: false, // ECOA — Figure owns the adverse-action notice
    primaryCta: FigureCtaAction.TalkToLoanOfficer,
    secondaryCta: null,
    showRefiAlternative: true,
    requiresComplianceApproval: true,
  },
  [FigureReasonCategory.PropertyValuation]: {
    discloseReason: true,
    primaryCta: FigureCtaAction.ContinueInPortal,
    secondaryCta: FigureCtaAction.TalkToLoanOfficer,
    showRefiAlternative: false,
    requiresComplianceApproval: false,
  },
  [FigureReasonCategory.CreditAccess]: {
    discloseReason: true,
    primaryCta: FigureCtaAction.ContinueInPortal, // handled by existing flows
    secondaryCta: FigureCtaAction.TalkToLoanOfficer,
    showRefiAlternative: false,
    requiresComplianceApproval: false,
  },
  [FigureReasonCategory.DataCorrection]: {
    discloseReason: true,
    primaryCta: FigureCtaAction.ContinueInPortal, // the ticket's hero flow
    secondaryCta: FigureCtaAction.TalkToLoanOfficer,
    showRefiAlternative: false,
    requiresComplianceApproval: false,
  },
  [FigureReasonCategory.IdentityFraud]: {
    discloseReason: false, // deliberately opaque
    primaryCta: FigureCtaAction.TalkToLoanOfficer,
    secondaryCta: null,
    showRefiAlternative: false, // don't encourage re-application
    requiresComplianceApproval: true,
  },
  [FigureReasonCategory.BorrowerChoice]: {
    discloseReason: true,
    primaryCta: FigureCtaAction.ContinueInPortal,
    secondaryCta: FigureCtaAction.TalkToLoanOfficer,
    showRefiAlternative: false,
    requiresComplianceApproval: false,
  },
  [FigureReasonCategory.Fallback]: {
    discloseReason: false,
    primaryCta: FigureCtaAction.TalkToLoanOfficer,
    secondaryCta: null,
    showRefiAlternative: true,
    requiresComplianceApproval: false,
  },
}

export function getFigureCategoryDisposition(
  category: FigureReasonCategory
): FigureCategoryDisposition {
  return FIGURE_CATEGORY_DISPOSITION[category]
}

export interface FigureCategoryCopy {
  headline: string
  /** Shown when the category's disposition has `discloseReason === true`. */
  body: string
  /** Shown when `discloseReason === false` — adverse-action/opaque safe. */
  genericBody: string
}

/**
 * Flips to `'APPROVED'` once product + legal finalize wording. Ship gate: the
 * no-offers redesign stays behind its feature flag until this is `'APPROVED'`.
 */
export const FIGURE_COPY_STATUS = 'PLACEHOLDER' as const

/**
 * ⚠️ PLACEHOLDER COPY — NOT COMPLIANCE-APPROVED. DO NOT SHIP AS-IS.
 * Real wording is owned by product + legal (see `requiresComplianceApproval`).
 * `body` is intentionally empty for categories whose disposition sets
 * `discloseReason: false` — those always render `genericBody`.
 */
export const FIGURE_CATEGORY_COPY: Record<
  FigureReasonCategory,
  FigureCategoryCopy
> = {
  [FigureReasonCategory.InsufficientEquity]: {
    headline: "Let's explore your options",
    body: "Based on the equity and information available, we weren't able to generate a HELOC rate quote. A loan officer can walk you through other options that may be available.",
    genericBody:
      "We couldn't put together a home equity offer right now. A loan officer can help you explore your options.",
  },
  [FigureReasonCategory.CreditDecline]: {
    headline: "Let's explore your options",
    body: '', // discloseReason is false for this category
    genericBody:
      "Based on the information provided, we weren't able to generate a HELOC rate quote. A loan officer can walk you through other options that may be available.",
  },
  [FigureReasonCategory.PropertyValuation]: {
    headline: 'We need to confirm a few details about your property',
    body: 'Continue your application to verify your property information and get a HELOC rate quote',
    genericBody:
      'We need a little more information about your property to continue. You can pick up right where you left off.',
  },
  [FigureReasonCategory.CreditAccess]: {
    headline: 'One quick step to see your offers',
    body: 'We need to confirm your credit information before we can show your offers. Continue your application to finish this step.',
    genericBody:
      'We need one more step before we can show your offers. Continue your application to finish.',
  },
  [FigureReasonCategory.DataCorrection]: {
    headline: "You're almost there",
    body: 'We just need you to confirm a few details before we can finalize your offer. Continue your application to pick up where you left off.',
    genericBody:
      'We need you to confirm a few details to continue. Pick up where you left off.',
  },
  [FigureReasonCategory.IdentityFraud]: {
    headline: "Let's finish this together",
    body: '', // never disclose
    genericBody:
      "We're not able to continue your application online. Please reach out to a loan officer so we can help you directly.",
  },
  [FigureReasonCategory.BorrowerChoice]: {
    headline: 'Ready to pick up where you left off?',
    body: 'Your application is paused. You can continue any time to review your options, or adjust your requested amount.',
    genericBody:
      'You can continue your application any time to review your options.',
  },
  [FigureReasonCategory.Fallback]: {
    headline: 'We may have another option for you',
    body: '', // discloseReason is false for this category
    genericBody:
      "Based on the information available, we weren't able to generate a HELOC rate quote. A loan officer can walk you through other options that may be available.",
  },
}

export function getFigureCategoryCopy(
  category: FigureReasonCategory
): FigureCategoryCopy {
  return FIGURE_CATEGORY_COPY[category]
}
